import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachMoneySharpIcon from '@mui/icons-material/AttachMoneySharp';
import MoneyOffSharpIcon from '@mui/icons-material/MoneyOffSharp';
import CategoryIcon from '@mui/icons-material/Category';
import SavingsIcon from '@mui/icons-material/Savings';
import BalanceIcon from '@mui/icons-material/Balance';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import logo from '../../assets/images/logo192.png';
import { LAYOUT } from '../../constants/layout';
import walletApi from '../../api/wallet';
import { clearAuthData } from '../../utils/auth';
import { useCurrency } from '../../contexts/CurrencyContext';
import { Currency } from '../../utils/Currency';

const navConfig = [
  { label: 'Home', icon: <HomeIcon />, path: '/home' },
  {
    label: 'Receita',
    icon: <AttachMoneySharpIcon />,
    key: 'cashin',
    children: [
      { label: 'Nova receita', path: '/cashin/register' },
      { label: 'Lista de receitas', path: '/cashin/list' },
      { label: 'Dashboard', path: '/cashin/dashboard' },
    ],
  },
  {
    label: 'Custo',
    icon: <MoneyOffSharpIcon />,
    key: 'cashout',
    children: [
      { label: 'Novo custo', path: '/cashout/register' },
      { label: 'Lista de custos', path: '/cashout/list' },
      { label: 'Planejamento anual', path: '/planejamento' },
      { label: 'Dashboard', path: '/cashout/dashboard' },
    ],
  },
  {
    label: 'Operações',
    icon: <SavingsIcon />,
    key: 'operations',
    children: [
      { label: 'Nova operação', path: '/operations/register' },
      { label: 'Lista de operações', path: '/operations/list' },
      { label: 'Posições', path: '/positions' },
    ],
  },
  { label: 'Categoria', icon: <CategoryIcon />, path: '/categories/list' },
  { label: 'Balance', icon: <BalanceIcon />, path: '/balance' },
];

const DashboardLayout = ({ children, colorMode, onToggleColorMode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [desktopDrawerOpen, setDesktopDrawerOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const { currency, setCurrency } = useCurrency();

  const userName = useMemo(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      return '';
    }
    try {
      const user = JSON.parse(storedUser);
      return user.Name || '';
    } catch {
      return '';
    }
  }, []);

  const userAvatarUrl = useMemo(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      return '';
    }
    try {
      const user = JSON.parse(storedUser);
      return user.AvatarURL || '';
    } catch {
      return '';
    }
  }, []);

  const handleToggleDrawer = () => {
    if (isDesktop) {
      setDesktopDrawerOpen((prev) => !prev);
      return;
    }
    setDrawerOpen((prev) => !prev);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (!isDesktop) {
      setDrawerOpen(false);
    }
  };

  const handleSectionToggle = (key) => (event) => {
    event.stopPropagation();
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleProfileOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setMenuAnchorEl(null);
  };

  const handleLogout = () => {
    walletApi.logout().catch(() => null);
    clearAuthData();
    navigate('/login');
  };

  const drawerContent = (
    <Box sx={{ p: 2, pt: { xs: 2, lg: 10 } }}>
      <Typography variant="overline" color="text.secondary" sx={{ px: 1, mb: 1, display: 'block' }}>
        Menu
      </Typography>
      <List disablePadding>
        {navConfig.map((item) => {
          const isActive = item.path && location.pathname === item.path;
          const isSectionOpen = item.key ? openSections[item.key] : false;

          if (item.children) {
            return (
              <Box key={item.label} sx={{ mb: 1 }}>
                <ListItemButton onClick={handleSectionToggle(item.key)} sx={{ borderRadius: 1 }}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                  {isSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
                <Collapse in={isSectionOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => {
                      const isChildActive = location.pathname === child.path;
                      return (
                        <ListItemButton
                          key={child.label}
                          onClick={() => handleNavigate(child.path)}
                          selected={isChildActive}
                          sx={{
                            pl: 4,
                            borderRadius: 1,
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          <ListItemText primary={child.label} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          return (
            <ListItemButton
              key={item.label}
              onClick={() => handleNavigate(item.path)}
              selected={isActive}
              sx={{
                mb: 1,
                borderRadius: 1,
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <IconButton
        color="inherit"
        onClick={handleToggleDrawer}
        sx={{
          display: { xs: 'none', lg: 'flex' },
          position: 'fixed',
          left: 16,
          top: 12,
          zIndex: theme.zIndex.drawer + 2,
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          '&:hover': {
            bgcolor: alpha(theme.palette.background.paper, 1),
          },
        }}
      >
        <MenuIcon />
      </IconButton>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
          width: {
            lg: `calc(100% - ${desktopDrawerOpen ? LAYOUT.NAV_WIDTH : 0}px)`,
          },
          ml: { lg: `${desktopDrawerOpen ? LAYOUT.NAV_WIDTH : 0}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: LAYOUT.APPBAR_HEIGHT }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleToggleDrawer}
            sx={{ display: { xs: 'flex', lg: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: { lg: 5 } }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{ height: 32, width: 32, cursor: 'pointer' }}
              onClick={() => handleNavigate('/home')}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, cursor: 'pointer' }}
              onClick={() => handleNavigate('/home')}
            >
              Wallet
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <ToggleButtonGroup
            value={currency}
            exclusive
            onChange={(_, newCurrency) => newCurrency && setCurrency(newCurrency)}
            size="small"
            sx={{
              mr: 1,
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                },
              },
            }}
          >
            <ToggleButton value={Currency.RealBRL}>BRL</ToggleButton>
            <ToggleButton value={Currency.DolarUSD}>USD</ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            onClick={onToggleColorMode}
            color="inherit"
            sx={{ mr: 1 }}
            aria-label="toggle color mode"
          >
            {colorMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <IconButton onClick={handleProfileOpen} sx={{ p: 0 }}>
            <Avatar
              src={userAvatarUrl || undefined}
              alt={userName || 'Usuario'}
              sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}
            >
              {userName ? userName.charAt(0).toUpperCase() : <AccountCircleIcon />}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleProfileClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => handleNavigate('/user/profile')}>
              <AccountCircleIcon sx={{ mr: 1 }} />
              Perfil
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToAppIcon sx={{ mr: 1 }} />
              Sair
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isDesktop ? 'persistent' : 'temporary'}
        open={isDesktop ? desktopDrawerOpen : drawerOpen}
        onClose={handleToggleDrawer}
        sx={{
          width: isDesktop && !desktopDrawerOpen ? 0 : LAYOUT.NAV_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isDesktop && !desktopDrawerOpen ? 0 : LAYOUT.NAV_WIDTH,
            boxSizing: 'border-box',
            borderRight: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
            bgcolor: 'background.paper',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          pt: { xs: 10, lg: 11 },
          px: { xs: 2, sm: 3, lg: 4 },
          pb: 3,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter,
          }),
          overflowX: 'auto',
        }}
      >
        <Box sx={{ flexGrow: 1, width: '100%' }}>{children}</Box>
        {/* <Footer /> */}
      </Box>
    </Box>
  );
};

export default DashboardLayout;
